// src/components/Customer/Customer.jsx
import React, { useState, useEffect } from 'react';
import customerService from '../../services/customerService';
import CustomerTable from './CustomerTable';
import CustomerModal from './CustomerModal';
import EditCustomerModal from './EditCustomerModal';
import { showNotification } from '../../utils/notification';
import '../Inventory/inventory.css';
import { PlusCircle } from 'lucide-react';

const Customer = ({ toggleSidebar }) => {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [filters, setFilters] = useState({ search: '' });
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [totalPages, setTotalPages] = useState(1);

  // Load routes ONCE on component mount
  useEffect(() => {
    const loadRoutes = async () => {
      try {
        const routesData = await customerService.getAvailableRoutes();
        console.log('Routes loaded:', routesData);
        setRoutes(routesData);
      } catch (error) {
        console.error('Failed to load routes:', error);
        showNotification(`Failed to load routes: ${error.message}`, 'error');
      }
    };
    loadRoutes();
  }, []);

  // Load customers when page changes
  useEffect(() => {
    loadData();
  }, [page]);

  // Apply filters whenever customers or filters change
  useEffect(() => {
    applyFilters();
  }, [customers, filters]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const customersData = await customerService.getAll(page, limit);
      console.log('Customers loaded:', customersData);
      setHasNextPage(customersData.length === limit);
      
      // Estimate total pages
      if (customersData.length < limit) {
        setTotalPages(page);
      } else {
        setTotalPages(page + 1);
      }
      
      setCustomers(customersData);
      showNotification(`Loaded ${customersData.length} customers`, 'success');
    } catch (error) {
      console.error('Error loading customers:', error);
      showNotification(`Failed to load: ${error.message}`, 'error');
      setCustomers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    const safeCustomers = Array.isArray(customers) ? customers : [];
    let filtered = [...safeCustomers];

    if (filters.search.trim()) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter((c) => {
        const name = c.name?.toLowerCase() || '';
        const shopName = c.shopName?.toLowerCase() || '';
        const town = c.town?.toLowerCase() || '';
        const contactNumber = c.contactNumber?.toLowerCase() || '';
        return (
          name.includes(searchLower) ||
          shopName.includes(searchLower) ||
          town.includes(searchLower) ||
          contactNumber.includes(searchLower)
        );
      });
    }

    setFilteredCustomers(filtered);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleClearFilters = () => {
    setFilters({ search: '' });
    showNotification('Filters cleared', 'info');
  };

  const handleAddNew = () => {
    setSelectedCustomer(null);
    setIsModalOpen(true);
  };

  const handleEdit = async (customer) => {
    console.log('Editing customer:', customer);
    console.log('Available routes:', routes);
    setSelectedCustomer(customer);
    setIsEditModalOpen(true);
  };

  const handleSave = async (payload) => {
    try {
      setIsSaving(true);
      await customerService.create(payload);
      showNotification(`Customer "${payload.name}" created successfully!`, 'success');
      setIsModalOpen(false);
      setPage(1);
      await loadData();
    } catch (error) {
      console.error('Error saving customer:', error);
      showNotification(`Failed to create: ${error.message}`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveEdit = async (payload) => {
    try {
      setIsSaving(true);
      
      console.log('Full customer object:', JSON.stringify(selectedCustomer, null, 2));
      console.log('Customer keys:', Object.keys(selectedCustomer));
      
      const customerId = selectedCustomer._id 
                      || selectedCustomer.id 
                      || selectedCustomer.customerId 
                      || selectedCustomer.customer_id;
      
      console.log('Customer ID:', customerId);
      console.log('Payload being sent:', payload);
      
      if (!customerId) {
        console.error('No ID found. Available properties:', Object.keys(selectedCustomer));
        throw new Error('Customer ID is missing.');
      }
      
      await customerService.update(customerId, payload);
      showNotification(`Customer "${payload.name}" updated successfully!`, 'success');
      setIsEditModalOpen(false);
      setSelectedCustomer(null);
      await loadData();
    } catch (error) {
      console.error('Update error:', error);
      showNotification(`Failed to update: ${error.message}`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (customer) => {
    if (!window.confirm(`Delete ${customer.name}?`)) return;
    try {
      const customerId = customer._id || customer.id;
      await customerService.delete(customerId);
      showNotification('Customer deleted successfully', 'success');
      setPage(1);
      await loadData();
    } catch (error) {
      console.error('Error deleting customer:', error);
      showNotification(`Failed to delete: ${error.message}`, 'error');
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  return (
    <main className="inventory-main-content">
      <header className="inventory-content-header">
        <button onClick={toggleSidebar} className="sidebar-toggle-btn" type="button">
          ☰
        </button>
        <h2 className="inventory-content-title">Customer Management</h2>
        <div className="header-actions">
          <button 
            className="inventory-btn inventory-btn-add" 
            onClick={handleAddNew}
            type="button"
          >
            <PlusCircle size={18} />
            Add New Customer
          </button>
        </div>
      </header>

      <section className="inventory-filter-controls">
        <div className="inventory-filter-group">
          <input 
            type="text"
            name="search"
            className="inventory-custom-input inventory-search-input" 
            placeholder="Search Name/Shop/Town/Contact" 
            value={filters.search} 
            onChange={handleFilterChange}
          />
          {/* <button 
            className="inventory-btn inventory-btn-primary"
            onClick={applyFilters}
            type="button"
          >
            Apply Filter
          </button> */}
          <button 
            className="inventory-btn inventory-btn-clear" 
            onClick={handleClearFilters}
            type="button"
          >
            Clear Filters
          </button>
        </div>
      </section>

      <CustomerTable 
        customers={filteredCustomers} 
        onEdit={handleEdit} 
        onDelete={handleDelete} 
        isLoading={isLoading} 
      />

      {/* Pagination Controls */}
      <div className="pagination-controls">
        <button
          className="btn-page"
          disabled={page === 1}
          onClick={() => setPage((prev) => prev - 1)}
          type="button"
        >
          Previous
        </button>
        
        <div className="page-numbers">
          {page > 2 && (
            <>
              <button className="btn-page-num" onClick={() => handlePageChange(1)}>1</button>
              {page > 3 && <span className="page-ellipsis">...</span>}
            </>
          )}
          
          {page > 1 && (
            <button 
              className="btn-page-num" 
              onClick={() => handlePageChange(page - 1)}
            >
              {page - 1}
            </button>
          )}
          
          <button className="btn-page-num active">{page}</button>
          
          {hasNextPage && (
            <button 
              className="btn-page-num" 
              onClick={() => handlePageChange(page + 1)}
            >
              {page + 1}
            </button>
          )}
          
          {hasNextPage && page < totalPages - 1 && (
            <>
              {page < totalPages - 2 && <span className="page-ellipsis">...</span>}
              <button className="btn-page-num" onClick={() => handlePageChange(totalPages)}>{totalPages}</button>
            </>
          )}
        </div>

        <span className="page-info">Page {page} of {totalPages || 1}</span>

        <button
          className="btn-page"
          disabled={!hasNextPage}
          onClick={() => setPage((prev) => prev + 1)}
          type="button"
        >
          Next
        </button>
      </div>

      <CustomerModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        isSaving={isSaving}
        routes={routes}
      />

      <EditCustomerModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedCustomer(null);
        }}
        onSave={handleSaveEdit}
        customer={selectedCustomer}
        isSaving={isSaving}
        routes={routes}
      />
    </main>
  );
};

export default Customer;